import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, CheckCircle, AlertCircle, CreditCard, Scale, Mail } from 'lucide-react';
import { useSchema } from '../hooks/useSchema';

const TermsOfService = () => {
  const lastUpdated = "March 10, 2025";
  
  // SEO Schema
  useSchema({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Terms of Service - CoRegulate",
    "description": "Read CoRegulate's Terms of Service for using our ADHD emotional regulation tools.",
    "url": "https://coregulateadhd.com/terms",
    "isPartOf": {
      "@type": "WebSite",
      "name": "CoRegulate",
      "url": "https://coregulateadhd.com"
    }
  });
  
  return (
    <>
      <Helmet>
        <title>Terms of Service | CoRegulate - ADHD Emotional Regulation App</title>
        <meta name="description" content="Read CoRegulate's Terms of Service. Understand your rights and responsibilities when using our ADHD emotional regulation tools and services." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://coregulateadhd.com/terms" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Terms of Service | CoRegulate" />
        <meta property="og:description" content="Read CoRegulate's Terms of Service for using our ADHD emotional regulation tools." />
        <meta property="og:url" content="https://coregulateadhd.com/terms" />
        <meta property="og:site_name" content="CoRegulate" />
        <meta property="og:image" content="https://coregulateadhd.com/og-image.png" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Terms of Service | CoRegulate" />
        <meta name="twitter:description" content="Read CoRegulate's Terms of Service for using our ADHD emotional regulation tools." />
        <meta name="twitter:image" content="https://coregulateadhd.com/og-image.png" />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="bg-card border-b border-border">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Home</span>
              <span className="sm:hidden">Back</span>
            </Link>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-12">
          {/* Title Section */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <FileText className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl font-serif text-foreground mb-4">Terms of Service</h1>
            <p className="text-muted-foreground">Last updated: {lastUpdated}</p>
          </div>

          {/* Introduction */}
          <div className="prose prose-slate max-w-none">
            <div className="bg-card rounded-2xl p-8 border border-border mb-8">
              <p className="text-lg text-foreground leading-relaxed">
                Welcome to CoRegulate. By using our app and services, you agree to these terms. 
                Please read them carefully. We've tried to keep them clear and straightforward.
              </p>
            </div>

            {/* Important Notice */}
            <div className="bg-amber-50 dark:bg-amber-950/30 rounded-xl p-6 border border-amber-200 dark:border-amber-800 mb-8 flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">Important Health Disclaimer</h3>
                <p className="text-amber-700 dark:text-amber-300 text-sm">
                  CoRegulate is a wellness tool designed to support emotional regulation. It is <strong>not</strong> a 
                  substitute for professional medical advice, diagnosis, or treatment. If you're experiencing a 
                  mental health crisis, please contact a healthcare professional or emergency services immediately.
                </p>
              </div>
            </div>

            {/* Sections */}
            <section className="mb-10">
              <h2 className="text-2xl font-serif text-foreground mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold text-primary">1</span>
                Acceptance of Terms
              </h2>
              <div className="bg-card rounded-xl p-6 border border-border">
                <p className="text-muted-foreground">
                  By creating an account or using CoRegulate, you agree to be bound by these Terms of Service 
                  and our Privacy Policy. If you don't agree to these terms, please don't use our services.
                </p>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-serif text-foreground mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold text-primary">2</span>
                Our Services
              </h2>
              <div className="bg-card rounded-xl p-6 border border-border space-y-4">
                <p className="text-muted-foreground">
                  CoRegulate provides:
                </p>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Emotional regulation tools and techniques</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Guided breathing exercises and meditations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>AI-powered companion for emotional support</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Progress tracking and emotional wellness insights</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Relationship tools for ADHD-affected partnerships</span>
                  </li>
                </ul>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-serif text-foreground mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold text-primary">3</span>
                Account Responsibilities
              </h2>
              <div className="bg-card rounded-xl p-6 border border-border space-y-4">
                <p className="text-muted-foreground">When you create an account, you agree to:</p>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span>Provide accurate and complete information</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span>Keep your password secure and confidential</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span>Be responsible for all activity under your account</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span>Notify us immediately of any unauthorized access</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span>Be at least 18 years old (or have parental consent)</span>
                  </li>
                </ul>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-serif text-foreground mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold text-primary">4</span>
                Subscriptions & Payments
              </h2>
              <div className="bg-card rounded-xl p-6 border border-border space-y-4">
                <div className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Subscription Plans</h3>
                    <p className="text-muted-foreground text-sm">
                      We offer free and paid subscription tiers. Paid plans provide access to premium features 
                      and are billed on a recurring basis (monthly or annually).
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Billing</h3>
                    <p className="text-muted-foreground text-sm">
                      Payments are processed securely through Stripe. By subscribing, you authorize us to 
                      charge your payment method on a recurring basis until you cancel.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Cancellation</h3>
                    <p className="text-muted-foreground text-sm">
                      You may cancel your subscription at any time through your account settings. 
                      You'll continue to have access until the end of your current billing period.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Refunds</h3>
                    <p className="text-muted-foreground text-sm">
                      We offer refunds on a case-by-case basis. If you're not satisfied with our service, 
                      please contact us within 14 days of your purchase.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-serif text-foreground mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold text-primary">5</span>
                Acceptable Use
              </h2>
              <div className="bg-card rounded-xl p-6 border border-border">
                <p className="text-muted-foreground mb-4">You agree not to:</p>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Use the service for any unlawful purpose</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Share your account credentials with others</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Attempt to gain unauthorized access to our systems</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Interfere with or disrupt the service</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Copy, modify, or distribute our content without permission</span>
                  </li>
                </ul>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-serif text-foreground mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold text-primary">6</span>
                Intellectual Property
              </h2>
              <div className="bg-card rounded-xl p-6 border border-border">
                <p className="text-muted-foreground">
                  All content, features, and functionality of CoRegulate — including text, graphics, logos, 
                  audio, video, and software — are owned by CoRegulate and protected by intellectual property laws. 
                  You may not reproduce, distribute, or create derivative works without our express permission.
                </p>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-serif text-foreground mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold text-primary">7</span>
                Limitation of Liability
              </h2>
              <div className="bg-card rounded-xl p-6 border border-border space-y-4">
                <div className="flex items-start gap-3">
                  <Scale className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <p className="text-muted-foreground">
                    CoRegulate is provided "as is" without warranties of any kind. We do not guarantee that 
                    the service will be uninterrupted, secure, or error-free.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <Scale className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <p className="text-muted-foreground">
                    To the maximum extent permitted by law, CoRegulate shall not be liable for any indirect, 
                    incidental, special, or consequential damages arising from your use of the service.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-serif text-foreground mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold text-primary">8</span>
                Changes to Terms
              </h2>
              <div className="bg-card rounded-xl p-6 border border-border">
                <p className="text-muted-foreground">
                  We may update these Terms of Service from time to time. We'll notify you of significant 
                  changes via email or through the app. Your continued use of CoRegulate after changes 
                  constitutes acceptance of the new terms.
                </p>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-serif text-foreground mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold text-primary">9</span>
                Termination
              </h2>
              <div className="bg-card rounded-xl p-6 border border-border">
                <p className="text-muted-foreground">
                  We reserve the right to suspend or terminate your account if you violate these terms. 
                  You may also delete your account at any time through your account settings. Upon termination, 
                  your right to use the service will immediately cease.
                </p>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-serif text-foreground mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold text-primary">10</span>
                Contact Us
              </h2>
              <div className="bg-card rounded-xl p-6 border border-border">
                <p className="text-muted-foreground mb-4">
                  If you have questions about these Terms of Service, please contact us:
                </p>
                <a 
                  href="mailto:legal@coregulateadhd.com" 
                  className="inline-flex items-center gap-2 text-primary hover:underline"
                >
                  <Mail className="w-4 h-4" />
                  legal@coregulateadhd.com
                </a>
              </div>
            </section>

            {/* Acceptance */}
            <div className="bg-primary/5 rounded-xl p-6 border border-primary/20 text-center">
              <CheckCircle className="w-8 h-8 text-primary mx-auto mb-3" />
              <p className="text-foreground font-medium">
                By using CoRegulate, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-card border-t border-border mt-12">
          <div className="max-w-4xl mx-auto px-4 py-8 text-center space-x-6">
            <Link to="/privacy" className="text-primary hover:underline">
              Privacy Policy
            </Link>
            <Link to="/" className="text-primary hover:underline">
              Return to CoRegulate
            </Link>
          </div>
        </footer>
      </div>
    </>
  );
};

export default TermsOfService;
